import { type ButtonHTMLAttributes, forwardRef, memo } from 'react';
import { Link as HeroUiLink, type LinkProps as HeroUiLinkProps } from '@heroui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useLinkParams } from 'NavigationHelpers';
import { type ClickableTextProps, type IconName } from './ClickableText.types';

function AnchorIcon({ iconName }: { iconName: IconName }) {
    switch (iconName) {
        case 'right-arrow':
            return <FontAwesomeIcon className="ms-1 rtl:rotate-180" icon={faArrowRight} />;
        case 'chevron-down':
            return <FontAwesomeIcon className="ltr:ml-1 rtl:mr-1" icon={faChevronDown} />;
        case 'chevron-up':
            return <FontAwesomeIcon className="ltr:ml-1 rtl:mr-1" icon={faChevronUp} />;
        default:
            return null;
    }
}

const VanillaButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
    ({ children, className, ...props }, ref) => (
        <button
            ref={ref}
            type="button"
            className={twMerge('h-auto min-w-0 gap-0 rounded-none bg-transparent px-0 antialiased', className)}
            {...props}
        >
            {children}
        </button>
    ),
);

function ClickableTextComponent({
    color = 'primary',
    weight = 'normal',
    anchorIcon = 'none',
    underline = 'always',
    target,
    href,
    allowWrap = false,
    children,
    className,
    onPress,
}: ClickableTextProps) {
    const classes = clsx(
        { 'text-slate-grey': color === 'slate-grey', 'text-black': color === 'black' },
        allowWrap ? 'inline' : '',
        {
            'font-normal': weight === 'normal',
            'font-medium': weight === 'medium',
            'font-semibold': weight === 'semibold',
        },
    );

    ({ href, target } = useLinkParams({ href, target }));

    const props = {
        className: twMerge(classes, 'text-base decoration-[0.8px] underline-offset-[3px]', className),
        underline,
        showAnchorIcon: anchorIcon !== 'none',
        anchorIcon: <AnchorIcon iconName={anchorIcon} />,
        href: href || undefined,
        target: target || undefined,
        onPress,
    } as Partial<HeroUiLinkProps>;

    // When we don't have an href, we render as a Button to provide better semantics for accessibility
    // (e.g. screen readers) since an `<a>` tag without an `href` attr is not considered valid HTML.
    if (!href) {
        props.as = VanillaButton;
    }

    // FIXME: special offlineMode handling like loadUrl has. https://trello.com/c/rpUhP05h

    // HeroUiLink will handle the link according to the rules defined in the navigate and useHref properties
    // passed to HeroUIProvider in the FrontRoyalRouter file
    return <HeroUiLink {...props}>{children}</HeroUiLink>;
}

export const ClickableText = memo(ClickableTextComponent) as typeof ClickableTextComponent;
